import {
	DndContext,
	type DragEndEvent,
	DragOverlay,
	type DragStartEvent,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import { ArrowDownAZ, ArrowUpZA, Clock } from "lucide-react";
import { useMemo, useState } from "react";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { TooltipProvider } from "@/components/ui/tooltip";
import type { IntegrationStatus, Wallet } from "@/types";
import { KanbanCard } from "./KanbanCard";
import { KanbanColumn } from "./KanbanColumn";
import { KANBAN_STAGES, type SortMode } from "./kanbanConfig";

const VISIBLE_KANBAN_STAGES = KANBAN_STAGES.filter((s) => !s.hiddenInKanban);

interface KanbanBoardProps {
	wallets: Wallet[];
	onMove?: (walletId: string, newStatus: IntegrationStatus) => void;
}

export function KanbanBoard({ wallets, onMove }: KanbanBoardProps) {
	const [activeWallet, setActiveWallet] = useState<Wallet | null>(null);
	const [sortMode, setSortMode] = useState<SortMode>("updated");

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
	);

	const walletsByStage = useMemo(() => {
		const map = new Map<string, Wallet[]>();
		for (const stage of VISIBLE_KANBAN_STAGES) {
			map.set(stage.id, []);
		}
		for (const wallet of wallets) {
			const stage = VISIBLE_KANBAN_STAGES.find((s) =>
				s.statuses.includes(wallet.integration_status),
			);
			if (stage) {
				map.get(stage.id)?.push(wallet);
			}
		}
		return map;
	}, [wallets]);

	const handleDragStart = (event: DragStartEvent) => {
		const wallet = wallets.find((w) => w.id === event.active.id);
		setActiveWallet(wallet ?? null);
	};

	const handleDragEnd = (event: DragEndEvent) => {
		setActiveWallet(null);
		const { active, over } = event;
		if (!over || !onMove) return;

		const walletId = active.id as string;
		const targetStageId = over.id as string;

		const targetStage = VISIBLE_KANBAN_STAGES.find(
			(s) => s.id === targetStageId,
		);
		if (!targetStage) return;

		// Check if already in this stage
		const wallet = wallets.find((w) => w.id === walletId);
		if (!wallet) return;
		if (targetStage.statuses.includes(wallet.integration_status)) return;

		onMove(walletId, targetStage.statuses[0]);
	};

	return (
		<TooltipProvider>
			<div className="mb-3 flex justify-end">
				<Select
					value={sortMode}
					onValueChange={(v) => setSortMode(v as SortMode)}
				>
					<SelectTrigger className="h-8 w-36 text-xs">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="updated">
							<span className="flex items-center gap-1.5">
								<Clock className="h-3 w-3" /> Recent
							</span>
						</SelectItem>
						<SelectItem value="alpha-asc">
							<span className="flex items-center gap-1.5">
								<ArrowDownAZ className="h-3 w-3" /> A → Z
							</span>
						</SelectItem>
						<SelectItem value="alpha-desc">
							<span className="flex items-center gap-1.5">
								<ArrowUpZA className="h-3 w-3" /> Z → A
							</span>
						</SelectItem>
					</SelectContent>
				</Select>
			</div>
			<DndContext
				sensors={sensors}
				onDragStart={handleDragStart}
				onDragEnd={handleDragEnd}
			>
				<div className="flex gap-4 overflow-x-auto pb-4">
					{VISIBLE_KANBAN_STAGES.map((stage) => (
						<KanbanColumn
							key={stage.id}
							stage={stage}
							wallets={walletsByStage.get(stage.id) ?? []}
							sortMode={sortMode}
						/>
					))}
				</div>
				<DragOverlay>
					{activeWallet ? (
						<KanbanCard wallet={activeWallet} isDragOverlay />
					) : null}
				</DragOverlay>
			</DndContext>
		</TooltipProvider>
	);
}
